import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useToasts } from "react-toast-notifications";
import { useUserContext } from "store/contexts/userContext";
import GeneralService from "services/GeneralService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faTicketAlt, 
  faExclamationCircle, 
  faCheckCircle, 
  faArrowLeft, 
  faPlus 
} from "@fortawesome/free-solid-svg-icons";

export default () => {
  const { user } = useUserContext();
  const { addToast } = useToasts();
  const history = useHistory();
  const location = useLocation();
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const prepopulatedBody = location.state?.postContent || "";
  const prepopulatedTitle = location.state?.postTitle || "";

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: prepopulatedTitle,
      email: user?.email || "",
      group: "Users",
      priority: "2 normal",
      body: prepopulatedBody,
    },
    validationSchema: Yup.object().shape({
      title: Yup.string()
        .min(5, "Title must be at least 5 characters.")
        .max(100, "Title cannot be more than 100 characters.")
        .required("Title is required."),
      email: Yup.string()
        .email("Please enter a valid email address.")
        .required("Email address is required."),
      group: Yup.string()
        .required("Group is required."),
      priority: Yup.string()
        .required("Priority is required."),
      body: Yup.string()
        .min(10, "Please describe your issue in at least 10 characters.")
        .max(2000, "Description cannot exceed 2000 characters.")
        .required("Description is required."),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setErrorMessage(null);

        const payload = {
          title: values.title,
          group: "Users",
          priority: values.priority,
          customer: values.email,
          article: {
            body: values.body,
          },
        };

        await GeneralService.createZammadTicket(payload);

        addToast("Incident logged successfully!", { appearance: "success" });
        setSuccess(true);
        formik.resetForm();
      } catch (error) {
        console.error("Failed to create Zammad ticket:", error);
        
        let apiError = "An unexpected error occurred. Please try again later.";
        if (error?.response?.data?.message) {
          apiError = error.response.data.message;
        } else if (error?.response?.data?.errors) {
          apiError = Object.values(error.response.data.errors).join(" ");
        }
        
        setErrorMessage(apiError);
        addToast("Failed to submit support ticket.", { appearance: "error" });
      } finally {
        setLoading(false);
      }
    },
  });

  const handleResetForm = () => {
    setSuccess(false);
    setErrorMessage(null);
  };

  const handleGoBack = () => {
    history.push("/dashboard");
  };

  return (
    <div className="support-ticket-container">
      <div className="support-ticket-card">
        {!success ? (
          <>
            <div className="card-header-section">
              <h2 className="ticket-header-title">
                <FontAwesomeIcon icon={faTicketAlt} style={{ marginRight: "12px", color: "#ffa100" }} />
                Support Center
              </h2>
              <p className="ticket-header-subtitle">
                Describe your issue below. We will automatically log this to our support ticketing system.
              </p>
            </div>

            {errorMessage && (
              <div className="alert alert-danger d-flex align-items-center" style={{ gap: "10px", borderRadius: "8px" }}>
                <FontAwesomeIcon icon={faExclamationCircle} />
                <span>{errorMessage}</span>
              </div>
            )}

            <form noValidate onSubmit={formik.handleSubmit}>
              {/* Incident Title */}
              <div className="form-group-custom">
                <label className="form-label-custom" htmlFor="title">
                  Incident Title / Summary
                </label>
                <input
                  id="title"
                  type="text"
                  placeholder="e.g. Need help with login assign to new user"
                  className={`form-control-custom ${
                    formik.errors.title && formik.touched.title ? "error-field" : ""
                  }`}
                  disabled={loading}
                  {...formik.getFieldProps("title")}
                />
                {formik.errors.title && formik.touched.title && (
                  <span className="error-text-custom">{formik.errors.title}</span>
                )}
              </div>

              {/* Email Address */}
              <div className="form-group-custom">
                <label className="form-label-custom" htmlFor="email">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="e.g. user@domain.com"
                  className={`form-control-custom ${
                    formik.errors.email && formik.touched.email ? "error-field" : ""
                  }`}
                  disabled={loading}
                  {...formik.getFieldProps("email")}
                />
                {formik.errors.email && formik.touched.email && (
                  <span className="error-text-custom">{formik.errors.email}</span>
                )}
              </div>

              <div className="row">
                {/* Ticketing Group */}
                <div className="col-md-6 col-sm-12">
                  <div className="form-group-custom">
                    <label className="form-label-custom" htmlFor="group">
                      Department / Group
                    </label>
                    <select
                      id="group"
                      className={`form-control-custom ${
                        formik.errors.group && formik.touched.group ? "error-field" : ""
                      }`}
                      disabled={true}
                      {...formik.getFieldProps("group")}
                    >
                      <option value="Users">Users</option>
                    </select>
                    {formik.errors.group && formik.touched.group && (
                      <span className="error-text-custom">{formik.errors.group}</span>
                    )}
                  </div>
                </div>

                {/* Priority */}
                <div className="col-md-6 col-sm-12">
                  <div className="form-group-custom">
                    <label className="form-label-custom" htmlFor="priority">
                      Priority Level
                    </label>
                    <select
                      id="priority"
                      className={`form-control-custom ${
                        formik.errors.priority && formik.touched.priority ? "error-field" : ""
                      }`}
                      disabled={loading}
                      {...formik.getFieldProps("priority")}
                    >
                      <option value="1 low">1 low</option>
                      <option value="2 normal">2 normal</option>
                      <option value="3 high">3 high</option>
                    </select>
                    {formik.errors.priority && formik.touched.priority && (
                      <span className="error-text-custom">{formik.errors.priority}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Description Body */}
              <div className="form-group-custom">
                <label className="form-label-custom" htmlFor="body">
                  Issue Description
                </label>
                <textarea
                  id="body"
                  rows="10"
                  placeholder="Provide all details about the problem you are experiencing..."
                  className={`form-control-custom ${
                    formik.errors.body && formik.touched.body ? "error-field" : ""
                  }`}
                  disabled={loading}
                  {...formik.getFieldProps("body")}
                />
                {formik.errors.body && formik.touched.body && (
                  <span className="error-text-custom">{formik.errors.body}</span>
                )}
              </div>

              {/* Submit / Action Button */}
              <div className="ticket-submit-btn-container">
                <button
                  type="submit"
                  className="ticket-submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="btn-spinner"></div>
                      <span>Logging incident...</span>
                    </>
                  ) : (
                    <span>Submit Support Ticket</span>
                  )}
                </button>
              </div>
            </form>
          </>
        ) : (
          /* Success Screen */
          <div className="ticket-success-container">
            <div className="success-icon-wrapper">
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <h3 className="success-title">Incident Logged Successfully!</h3>
            <p className="success-message">
              Thank you for reporting this issue. A ticket has been generated in Zammad. 
              Our support team will look into it shortly.
            </p>
            <div className="success-actions">
              <button 
                onClick={handleResetForm} 
                className="btn-success-action btn-success-primary"
              >
                <FontAwesomeIcon icon={faPlus} style={{ marginRight: "8px" }} />
                Create New Ticket
              </button>
              <button 
                onClick={handleGoBack} 
                className="btn-success-action btn-success-secondary"
              >
                <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: "8px" }} />
                Go to Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
